<template>
  <article v-show="!isClosed && !isDismissed" class="satisfaction-feedback">
    <EButton
      icon="close"
      class="close-button"
      variant="bare"
      data-test-id="sfe-dismiss-button"
      @click.stop="onClose('button')"
    />
    <div class="content">
      <div v-if="!isSubmitted">
        <p class="intro" data-test-id="sfe-intro">
          {{ $t(question.translationString) }}
        </p>

        <div class="ratings">
          <RatingIcon
            v-for="rating in ratings"
            :key="rating.value"
            :rating="rating"
            :on-click="onRatingIconClick"
            :is-active="activeValue === rating.value"
            :is-touched="isTouched"
          />
        </div>

        <transition name="toggle">
          <div v-if="isTouched" class="more-form">
            <div class="more-form__content">
              <ETextarea
                v-model="message"
                :placeholder="$t('common.header.feedback.tellmore')"
                autoscale
              />
              <div class="more-form-button">
                <EButton variant="solid-green" size="s" @click="submit">
                  {{ $t('web.green.satisfactionfeedback.entity.submit') }}
                </EButton>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <div v-if="isSubmitted">
        <div class="thankyou__wrapper">
          <EIcon icon="check-circle" size="m" />
          <EHeading as="h3" class="thankyou__heading">
            {{ $t('web.green.satisfactionfeedback.submitted.headline') }}
          </EHeading>
        </div>
        <p>
          {{ $t('web.green.satisfactionfeedback.submitted.text') }}
        </p>
      </div>
    </div>
    <div
      v-if="!isDesktopUp"
      class="satisfaction-feedback__footer-wave"
    >
      <EWaveMainline aria-hidden="true" />
    </div>
    <div
      v-if="isDesktopUp"
      class="satisfaction-feedback__footer-wave"
    >
      <EWaveSidebar aria-hidden="true" />
    </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import ETextarea from '~~/common/vue/components/textarea';
import EButton from '~~/common/vue/components/button';
import EHeading from '~~/common/vue/components/heading';
import EIcon from '~~/common/vue/components/icon';
import { satisfactionFeedbackEntitySubmit, satisfactionFeedbackEntityScoreClick, satisfactionFeedbackEntityDismiss, satisfactionFeedbackEntityDisplay } from '~~/search/common/js/client/analytics/satisfaction-feedback-entity';
import { satisfactionFeedbackEntity } from '~~/search/common/js/client/feature-flags';
import RatingIcon from './RatingIcon';
import screen from '~~/common/vue/mixins/screen';

export const SHOW_PROBABILITY = 5; // 0-100 probability of showing this component
export const OVERRIDE_PARAM_KEY = 'sfe-show-probability';
export const LOCALSTORAGE_KEY = 'ecosiaSfeHiddenUntil';
export const SFE_NO_INTERACTION_KEY = 'ecosiaSfeNoInteraction';
export const QUESTIONS = [
  { trackingId: 'customer_satisfaction_score', translationString: 'web.green.satisfactionfeedback.entity.headline' },
  { trackingId: 'green_user_value', translationString: 'web.green.satisfactionfeedback.entity.sustainabledecisions' },
];

export default {
  name: 'SatisfactionFeedbackEntity',
  components: {
    ETextarea,
    RatingIcon,
    EButton,
    EHeading,
    EIcon,
    EWaveMainline: async () => import(
      '~~/search/common/vue/assets/img/waves-footer-mainline.svg'
    ),
    EWaveSidebar: async () => import(
      '~~/search/common/vue/assets/img/waves-footer-sidebar.svg'
    ),
  },
  mixins: [screen],
  data() {
    return {
      activeValue: '',
      isClosed: false,
      isSubmitted: false,
      message: '',
      isDismissed: true,
      // select a random question from the question pool
      question: QUESTIONS[Math.floor(Math.random() * QUESTIONS.length)],
    };
  },
  computed: {
    ...mapGetters('unleash', ['getFeatureToggle']),
    isExperimentEnabled() {
      return this.getFeatureToggle(satisfactionFeedbackEntity.flag)?.value ===
             satisfactionFeedbackEntity.variants.enabled;
    },
    ratings() {
      return [
        { value: 1, id: 'very-unhappy' },
        { value: 2, id: 'unhappy' },
        { value: 3, id: 'indifferent' },
        { value: 4, id: 'happy' },
        { value: 5, id: 'very-happy' },
      ];
    },
    isTouched() {
      return this.activeValue !== '';
    },
    getShowProbability() {
      // to control the value via url set a param eg. "/search?q=ecosia&sfe-show-probability=100"
      let probabilityOverride = null;
      if (this.$route) {
        const { query } = this.$route;
        probabilityOverride = query[OVERRIDE_PARAM_KEY];
      }
      return probabilityOverride ?? SHOW_PROBABILITY;
    },
    randomNum100() {
      return Math.floor(Math.random() * 101); // random num between 1 and 100
    },
  },
  beforeMount() {
    if (!this.isExperimentEnabled) {
      return;
    }

    // if submitted or dismissed it is not shown for 30 days
    const hiddenUntilFromStorage = localStorage.getItem(LOCALSTORAGE_KEY);
    const notInteracted = localStorage.getItem(SFE_NO_INTERACTION_KEY);
    const isWithin30Days = hiddenUntilFromStorage && hiddenUntilFromStorage > Date.now();
    // SFE was previously displayed and not interracted (dismissed or rated) with in 30days,
    // show SFE, till a form of interaction occurs.
    if (notInteracted && !isWithin30Days) {
      this.isDismissed = false;
      return;
    }

    if (isWithin30Days) {
      // still within 30 days lockout -> dismissed
      this.isDismissed = true;
      return;
    }

    // randomization to only show this to a % of users
    const randomNum = this.randomNum100;
    if (randomNum <= this.getShowProbability) {
      this.isDismissed = false;
      this.setNonInteraction();
    }
  },
  mounted() {
    if (!this.isDismissed) {
      this.$track(satisfactionFeedbackEntityDisplay(this.question.trackingId));
    }
  },
  methods: {
    onRatingIconClick(value) {
      const scoreTracker = satisfactionFeedbackEntityScoreClick(value, this.question.trackingId);
      this.$track(scoreTracker);
      this.activeValue = value;
    },
    onClose() {
      this.setHiddenUntil();
      if (!this.isSubmitted) {
        this.$track((satisfactionFeedbackEntityDismiss(this.question.trackingId)));
      }
      this.isClosed = true;
    },
    submit() {
      const submitTracker = satisfactionFeedbackEntitySubmit(
        this.activeValue, this.message, this.question.trackingId,
      );
      this.$track(submitTracker);
      this.isSubmitted = true;
      this.setHiddenUntil();
    },
    setHiddenUntil() {
      // get date: now + 30 days
      const expireDate = new Date();
      expireDate.setDate(expireDate.getDate() + 30);

      // save date in miliseconds in localstorage
      localStorage.setItem(LOCALSTORAGE_KEY, Date.parse(expireDate));
      this.clearNonInteraction();
    },
    setNonInteraction() {
      localStorage.setItem(SFE_NO_INTERACTION_KEY, true);
    },
    clearNonInteraction() {
      localStorage.removeItem(SFE_NO_INTERACTION_KEY);
    },
  },
};
</script>
<style lang="scss" scoped>
.satisfaction-feedback {
  position: relative;
  margin-top: $space-s;
  background-color: var(--color-background-quaternary);

  @include desktop {
    margin-top: $space-m;
    background-color: var(--color-background-secondary);
    border-radius: $space-s $space-s 0 0;
  }
}

.content {
  padding: $space-l $space-m;
}

.satisfaction-feedback__footer-wave {
  width: 100%;
  height: auto;
  color: var(--color-background-primary);
  overflow: hidden;
}

.close-button {
  position: absolute;
  top: $space-1s;
  right: $space-1s;
}

.intro {
  font-size: $font-m;
  margin-top: 0;
  max-width: 80%;
}

.ratings {
  display: flex;
  margin-bottom: $space-s;
}

.more-form {
  max-height: 450px;
  overflow: hidden;
}

.more-form button {
  margin-top: $space-m;
  margin-bottom: $space-m;
}

.toggle-enter-active,
.toggle-leave-active {
  transition: max-height $timing-m;
}

.toggle-enter,
.toggle-leave-to {
  max-height: 0;
}

.thankyou__wrapper {
  display: flex;
  align-content: center;
  align-items: center;
  color: green;
}

.thankyou__heading {
  font-size: $font-1l;
  font-weight: $font-weight-1l;
  color: var(--color-text-primary);
  margin-left: $space-2s;
}
</style>

<style lang="scss">
@include desktop {
  .mainline .satisfaction-feedback {
    display: none;
  }
}
</style>
